import React, { useEffect, useMemo, useState } from 'react';
import styles from '../insertsPages.module.css';
import { Tabs } from '../../../Tabs';
import { Chooser } from '../../../Chooser';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { Visualiser, VisualiserTextBox } from '../../../Visualiser/index';
import { getInsertsAndCardsProduct } from '../../../../utilities/visualiserVariants.js';

/**
 * InsertThemedMessage component allows users to add a themed message for their box insert.
 *
 * @param {Object} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function InsertThemedMessage({ ...rest }) {
  const { getTextInput, setTextInput, setVisualiserSelection, clearVisualiserSelection } = useUserInput();

  const [activeIndex, setActiveIndex] = useState(0);

  const themedProduct = useMemo(() => {
    return getInsertsAndCardsProduct({ productKey: 'themedInserts' });
  }, []);

  const textVariants = useMemo(() => {
    if (!themedProduct || !Array.isArray(themedProduct.variants)) return [];

    return themedProduct.variants.filter((variant) => {
      const option1 = typeof variant?.option1 === 'string' ? variant.option1 : '';
      return option1 === 'Text';
    });
  }, [themedProduct]);

  const [selectedVariantId, setSelectedVariantId] = useState(
    textVariants && textVariants.length > 0 ? textVariants[0].id : null
  );

  useEffect(() => {
    // Keep selection valid when variants list loads or changes.
    if (!textVariants || textVariants.length === 0) return;
    if (!selectedVariantId || !textVariants.find((variant) => variant.id === selectedVariantId)) {
      setSelectedVariantId(textVariants[0].id);
    }
  }, [textVariants, selectedVariantId]);

  const selectedVariant = useMemo(() => {
    return textVariants.find((variant) => variant.id === selectedVariantId) || null;
  }, [textVariants, selectedVariantId]);

  useEffect(() => {
    const productKey = 'themedInserts';
    setVisualiserSelection({
      productKey,
      variant: selectedVariant,
      config: {
        templateId: 'INSERT_THEMED_TEXT_ONLY',
        mapping: {
          text: {
            'insertThemedMessage.back': 'UVBackText'
          }
        }
      }
    });
  }, [selectedVariant, setVisualiserSelection]);

  useEffect(() => {
    return () => {
      clearVisualiserSelection();
    };
  }, [clearVisualiserSelection]);

  const backgroundImageUrl = useMemo(() => {
    const raw = selectedVariant?.featured_image?.src || selectedVariant?.featured_media?.preview_image?.src || null;
    if (!raw || typeof raw !== 'string') return null;
    return raw.startsWith('/' + '/') ? `https:${raw}` : raw;
  }, [selectedVariant]);

  return (
    <div className={styles['visualiser-stack']}>
      <div className={styles['page--container']}>
        <Tabs
          tabs={[
            { id: 'front', label: 'Front' },
            { id: 'back', label: 'Back' }
          ]}
          activeIndex={activeIndex}
          onChange={setActiveIndex}
          fullWidth={true}
        />

        {activeIndex === 0 && (
          <Visualiser type="INSERT" background={backgroundImageUrl ? `url(${backgroundImageUrl})` : 'var(--color-white)'} subtitle="Not editable">
            {' '}
          </Visualiser>
        )}

        {activeIndex === 1 && (
          <Visualiser
            type="INSERT"
            background={`url(${window?.giftWrapData?.insertsAndCards?.images?.insertBack})`}
            subtitle="Tap to edit"
          >
            <VisualiserTextBox
              size={{
                width: '280px',
                minWidth: '200px',
                maxWidth: '280px',
                minHeight: '50px',
                maxHeight: '200px'
              }}
              position={{
                top: '50%',
                left: '50%'
              }}
              fontWeight="bold"
              maxLength={100}
              maxLines={8}
              lineHeight={1.2}
              fontSize={21}
              style={{
                transform: 'translate(-50%, -50%)'
              }}
              value={getTextInput('insertThemedMessage.back')}
              setValue={(value) => setTextInput('insertThemedMessage.back', value)}
            />
          </Visualiser>
        )}
      </div>

      {textVariants.length > 0 && (
        <Chooser
          title="Choose a themed insert design"
          subtitle={
            selectedVariant
              ? `${selectedVariant.public_title || selectedVariant.title || 'Design selected'}`.replace('Text / ', '')
              : 'Select a design'
          }
        >
          <div className={styles['chooser--content']}>
            <div className={styles['stock-card--chooser-grid']}>
              {textVariants.map((variant) => {
                const variantImg =
                  (variant?.featured_image && variant.featured_image.src) ||
                  variant?.featured_media?.preview_image?.src ||
                  null;
                const variantImgUrl =
                  variantImg && typeof variantImg === 'string'
                    ? variantImg.startsWith('/' + '/')
                      ? `https:${variantImg}`
                      : variantImg
                    : null;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    className={`${styles['stock-card--chooser-item']} ${
                      selectedVariantId === variant.id ? styles['stock-card--chooser-item--selected'] : ''
                    }`}
                    onClick={() => setSelectedVariantId(variant.id)}
                  >
                    {variantImgUrl ? <img src={variantImgUrl} alt={variant.title || 'Insert design'} /> : null}
                    <p>{`${variant.public_title || variant.title || 'Insert design'}`.replace('Text / ', '')}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </Chooser>
      )}
    </div>
  );
}

